import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { componentTypes } from "./componentTypes";
import PropertyEditor from "./PropertyEditor";
import ConditionalForm from "./ConditionalForm";

// Import atomic design components
import Button from "new_components/atoms/Button";
import Text from "new_components/atoms/Text";
import Tabs from "new_components/molecules/Tabs";
import Icon from "new_components/atoms/Icon";
import { ICON_SET } from "new_components/atoms/Icon/IconSet";
import colors from "styles/colors";
import typography from "styles/typography";

// Specialized editors
import ConsentEditor from "./ConsentEditor";
import InformationEditor from "./InformationEditor";

const SettingsDrawer = ({
  selectedItem,
  onClose,
  onDelete,
  droppedItems,
  setDroppedItems,
  skipOptions,
  categories,
  setCategories
}) => {
  const [localProperties, setLocalProperties] = useState({});
  const [conditionalData, setConditionalData] = useState({});
  const [activeTab, setActiveTab] = useState("settings");

  // For multiSelect and date, use dropdown's conditional support settings
  const getConditionalSupport = (type) => {
    if (type === 'multiSelect' || type === 'date') {
      return componentTypes['dropdown']?.supportsConditionals || false;
    }
    return componentTypes[type]?.supportsConditionals || false;
  };

  // Get the component definition to use for properties and conditionals
  const getComponentDefinition = (type) => {
    if (type === 'multiSelect' || type === 'date') {
      return componentTypes['dropdown'];
    }
    return componentTypes[type];
  };

  /**
   * Whenever selectedItem changes, copy its properties and conditionals
   * into local state. This avoids overwriting local changes on tab-switches.
   */
  useEffect(() => {
    if (selectedItem) {
      // Copy question properties
      setLocalProperties({ ...selectedItem.properties });
      // Copy conditionals (if any)
      setConditionalData(selectedItem.conditionals || { conditionals: [] });
    }
  }, [selectedItem]);

  /**
   * If the selectedItem does NOT support conditionals, force "Settings" tab.
   * This effect can depend on both selectedItem and activeTab, but only
   * changes the tab if the item doesn't support conditionals.
   */
  useEffect(() => {
    if (
      selectedItem &&
      !getConditionalSupport(selectedItem.type) &&
      activeTab === "conditionals"
    ) {
      setActiveTab("settings");
    }
  }, [selectedItem, activeTab]);

  if (!selectedItem) return null;

  /**
   * Auto-save function: merges localProperties + conditionalData back into
   * the main SurveyBuilder's droppedItems immediately on every change.
   */
  const autoSaveToBuilder = (newProps, newConditionals) => {
    setDroppedItems((items) =>
      items.map((item) =>
        item.uniqueId === selectedItem.uniqueId
          ? {
              ...item,
              properties: { ...newProps },
              conditionals: { ...newConditionals },
            }
          : item
      )
    );
  };

  // Called from either the default PropertyEditor or your custom editor
  const handlePropertyChange = (propName, newValue) => {
    setLocalProperties((prev) => {
      const updatedProps = { ...prev, [propName]: newValue };
      
      // Check if we need to update the component type based on property changes
      let updatedType = selectedItem.type;
      
      // Handle dropdown type modifications
      if (selectedItem.type === 'dropdown' || selectedItem.type === 'multiSelect' || selectedItem.type === 'date') {
        // Case 1: If "Allow multiple selections" is toggled
        if (propName === 'Allow multiple selections') {
          // If true, change to multiSelect, unless it's a date
          if (newValue && updatedProps.categoryType !== 'date') {
            updatedType = 'multiSelect';
          } 
          // If false, change back to dropdown, unless it's a date
          else if (!newValue && updatedProps.categoryType !== 'date') {
            updatedType = 'dropdown';
          }
        } 
        // Case 2: If "category" is changed
        else if (propName === 'category') {
          // Find the category type in categories
          const categoryObj = categories.find((cat) => cat.name === newValue);
          if (categoryObj) {
            // Store the category type in properties
            updatedProps.categoryType = categoryObj.type;
            
            // If category type is 'date', set the type to 'date'
            if (categoryObj.type === 'date') {
              updatedType = 'date';
            } else {
              // Otherwise, check if it should be multiSelect or dropdown
              updatedType = updatedProps['Allow multiple selections'] ? 'multiSelect' : 'dropdown';
            }
          }
        }
        
        // Update the type in the droppedItems array if changed
        if (updatedType !== selectedItem.type) {
          setDroppedItems(items =>
            items.map(item =>
              item.uniqueId === selectedItem.uniqueId
                ? { ...item, type: updatedType }
                : item
            )
          );
        }
      }
      
      autoSaveToBuilder(updatedProps, conditionalData);
      return updatedProps;
    });
  };

  const handleConditionalsChange = (updatedConditionals) => {
    setConditionalData((prev) => {
      const newCond = { ...prev, ...updatedConditionals };
      autoSaveToBuilder(localProperties, newCond);
      return newCond;
    });
  };

  const componentDefinition = getComponentDefinition(selectedItem.type);
  const supportsConditionals = getConditionalSupport(selectedItem.type);

  const isConsent = selectedItem.type === "consent";
  const isInformation = selectedItem.type === "information" || selectedItem.type === "end_screen";

  // Define tabs for the Tabs component
  const tabsConfig = [
    { label: "Settings", id: "settings" },
    ...(supportsConditionals ? [{ label: "Conditionals", id: "conditionals" }] : []),
  ];

  // Get the display name for the component type, handling converted types
  const getDisplayName = () => {
    // For multiSelect and date types, we need to use dropdown's configuration
    if (selectedItem.type === 'multiSelect') {
      return 'Multi-Select';
    } else if (selectedItem.type === 'date') {
      return 'Date Selection';
    } else if (componentTypes[selectedItem.type]) {
      return componentTypes[selectedItem.type].name;
    } else {
      return 'Unknown';
    }
  };


  return (
    <>
      <DrawerOverlay onClick={onClose} />
      <DrawerPanel>
        <DrawerHeader>
          <HeaderContent>
            <Icon 
              name={ICON_SET.gear} 
              size={20} 
              color={colors.primary[500]} 
            />
            <DrawerTitle>
              <Text 
                typographyStyle={typography.subhead.semibold}
                color={colors.text.dark}
              >
                {getDisplayName()} Settings
              </Text>
            </DrawerTitle>
          </HeaderContent>
          <Buttons>
            <Button 
              variant="secondary" 
              onClick={onDelete}
              style={{ marginRight: '8px' }}
            >
              <Icon 
                name={ICON_SET.delete} 
                size={16} 
                color={colors.auxiliary.red[500]} 
                style={{ marginRight: '4px' }}
              />
              Delete
            </Button>
            <Button 
              variant="primary" 
              onClick={onClose}
            >
              <Icon 
                name={ICON_SET.close} 
                size={16} 
                color={colors.background.white} 
                style={{ marginRight: '4px' }}
              />
              Close
            </Button>
          </Buttons>
        </DrawerHeader>

        <DrawerBody>
          {/* Use the Tabs component from your design system */}
          <StyledTabs
            tabs={tabsConfig}
            activeTabIndex={tabsConfig.findIndex(tab => tab.id === activeTab)}
            onTabChange={(index) => setActiveTab(tabsConfig[index].id)}
          />

          {/* Tab content: SETTINGS */}
          {activeTab === "settings" && (
            <TabContent>
              {isConsent && (
                <ConsentEditor
                  blocks={localProperties.blocks || []}
                  onChange={(updatedBlocks) =>
                    handlePropertyChange("blocks", updatedBlocks)
                  }
                />
              )}

              {isInformation && (
                <InformationEditor
                  blocks={localProperties.blocks || []}
                  onChange={(updatedBlocks) =>
                    handlePropertyChange("blocks", updatedBlocks)
                  }
                />
              )}

              {!isConsent && !isInformation && (
                <PropertyEditor
                  properties={localProperties}
                  onChange={handlePropertyChange}
                  definition={componentDefinition.properties}
                  categories={categories}
                  onCategoriesChange={setCategories}
                  componentType={selectedItem.type} 
                />
              )}
            </TabContent>
          )}

          {/* Tab content: CONDITIONALS */}
          {activeTab === "conditionals" && supportsConditionals && (
            <TabContent>
              <ConditionalForm
                initialData={conditionalData}
                onChange={handleConditionalsChange}
                skipOptions={skipOptions}
                currentQuestionConfig={componentDefinition}
                localProperties={localProperties}
              />
            </TabContent>
          )}
        </DrawerBody>
      </DrawerPanel>
    </>
  );
};

export default SettingsDrawer;

/* --- STYLES --- */

/** Dim overlay behind the drawer */
const DrawerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2px);
  z-index: 1000;
`;

/** Keyframe for the slide-in animation */
const slideInFromRight = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0%);
  }
`;

/** Panel that slides in from the right */
const DrawerPanel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 520px;
  height: 100%;
  background: ${colors.background.white};
  box-shadow: -2px 0 12px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  animation: ${slideInFromRight} 0.3s ease forwards;
  border-radius: 8px 0 0 8px;
`;

const DrawerHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.neutral[200]};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
`;

const DrawerTitle = styled.div`
  margin-left: 8px;
`;

const Buttons = styled.div`
  display: flex;
  gap: 8px;
`;

const DrawerBody = styled.div`
  flex: 1;
  padding: 24px;
  overflow-y: auto;
  background-color: ${colors.background.lightBlue};
`;

/** Styled version of the Tabs component */
const StyledTabs = styled(Tabs)`
  margin-bottom: 24px;
`;

/** Fade in animation for tab content */
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

/** Tab content wrapper */
const TabContent = styled.div`
  animation: ${fadeIn} 0.3s ease-in-out;
  background-color: ${colors.background.white};
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;
